import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Search, MapPin, Music, Calendar } from 'lucide-react'
import { useCurrentOrganization } from '~/auth/use-current-organization'
import { Button } from '~/ui/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '~/ui/command'
import { resolveSearchSelectionPath, type SearchResultType } from '~/layouts/search-form-utils'

const SEARCH_QUERY = gql(`
  query GlobalSearch($organizationId: ID!) {
    zones(organizationId: $organizationId, first: 50) {
      nodes {
        id
        name
        location {
          name
        }
      }
    }
    playlists(organizationId: $organizationId, first: 50) {
      nodes {
        id
        name
        playlistType
      }
    }
    tracks(first: 50) {
      nodes {
        id
        name
        artists {
          name
        }
        album {
          name
        }
      }
    }
    playlistSchedules(organizationId: $organizationId, first: 50) {
      nodes {
        id
        playlist {
          id
          name
        }
        zone {
          id
          name
        }
        startTime
        endTime
      }
    }
    voiceMessages(organizationId: $organizationId) {
      id
      displayName
      text
    }
  }
`)

export function SearchForm() {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { id: organizationId } = useCurrentOrganization()
  const navigate = useNavigate()

  const { data, loading } = useQuery(SEARCH_QUERY, {
    variables: { organizationId },
    skip: !organizationId || !open,
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  const handleSelect = (type: SearchResultType, id: string) => {
    setOpen(false)
    setSearchQuery('')
    navigate(resolveSearchSelectionPath(type, id))
  }

  const filterItems = (items: any[], query: string) => {
    if (!query) return items
    const lowerQuery = query.toLowerCase()

    return items.filter(item => {
      const itemName = item.name || ''
      const locationName = item.location?.name || ''
      const zoneName = item.zone?.name || ''
      const playlistName = item.playlist?.name || ''
      const displayName = item.displayName || ''
      const artistNames = item.artists?.map((artist: any) => artist.name || '').join(' ') || ''
      const albumName = item.album?.name || ''
      const playlistType = item.playlistType || ''
      const text = item.text || ''

      const searchableText = `${itemName} ${displayName} ${locationName} ${zoneName} ${playlistName} ${artistNames} ${albumName} ${playlistType} ${text}`.toLowerCase()

      const matches = searchableText.includes(lowerQuery)


      return matches
    })
  }

  const zones = data?.zones?.nodes || []
  const playlists = data?.playlists?.nodes || []
  const tracks = data?.tracks?.nodes || []
  const schedules = data?.playlistSchedules?.nodes || []
  const voiceMessages = data?.voiceMessages || []

  const filteredZones = searchQuery.length >= 2 ? filterItems(zones, searchQuery) : []
  const filteredPlaylists = searchQuery.length >= 2 ? filterItems(playlists, searchQuery) : []
  const filteredTracks = searchQuery.length >= 2 ? filterItems(tracks, searchQuery) : []
  const filteredVoiceMessages = searchQuery.length >= 2 ? filterItems(voiceMessages, searchQuery) : []
  const filteredSchedules = searchQuery.length >= 2 ? filterItems(schedules, searchQuery) : []

  const hasResults = filteredZones.length > 0 || filteredPlaylists.length > 0 || filteredTracks.length > 0 || filteredVoiceMessages.length > 0 || filteredSchedules.length > 0

  return (
    <>
      <Button
        variant="outline"
        className="relative h-8 w-full justify-start rounded-[0.5rem] bg-background text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-40 lg:w-64"
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="hidden lg:inline-flex">Search...</span>
        <span className="inline-flex lg:hidden">Search...</span>
        <kbd className="pointer-events-none absolute right-[0.3rem] top-[0.3rem] hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search zones, playlists, tracks, schedules, voice messages..."
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          {hasResults ? (
            <div className="space-y-2 min-h-[200px]">
              {filteredZones.length > 0 && (
                <CommandGroup heading="Zones" forceMount={true}>
                  {filteredZones.map((zone) => (
                    <CommandItem
                      key={zone.id}
                      value={`zone-${zone.id}`}
                      onSelect={() => handleSelect('zone', zone.id)}
                      forceMount={true}
                    >
                      <MapPin className="mr-2 h-4 w-4" />
                      <span>{zone.name}</span>
                      {zone.location && (
                        <span className="ml-2 text-muted-foreground text-xs">
                          in {zone.location.name}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {filteredPlaylists.length > 0 && (
                <CommandGroup heading="Playlists" forceMount={true}>
                  {filteredPlaylists.map((playlist) => (
                    <CommandItem
                      key={playlist.id}
                      value={`playlist-${playlist.id}`}
                      onSelect={() => handleSelect('playlist', playlist.id)}
                      forceMount={true}
                    >
                      <Music className="mr-2 h-4 w-4" />
                      <span>{playlist.name}</span>
                      <span className="ml-2 text-muted-foreground text-xs">
                        ({playlist.playlistType?.toLowerCase()})
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {filteredTracks.length > 0 && (
                <CommandGroup heading="Tracks" forceMount={true}>
                  {filteredTracks.map((track) => (
                    <CommandItem
                      key={track.id}
                      value={`track-${track.id}`}
                      onSelect={() => handleSelect('track', track.id)}
                      forceMount={true}
                    >
                      <Music className="mr-2 h-4 w-4" />
                      <span>{track.name}</span>
                      <span className="ml-2 text-muted-foreground text-xs">
                        by {track.artists?.map((artist: any) => artist.name).join(', ')}
                        {track.album && ` • ${track.album.name}`}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {filteredSchedules.length > 0 && (
                <CommandGroup heading="Schedules" forceMount={true}>
                  {filteredSchedules.map((schedule) => (
                    <CommandItem
                      key={schedule.id}
                      value={`schedule-${schedule.id}`}
                      onSelect={() => handleSelect('schedule', schedule.id)}
                      forceMount={true}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      <span>{schedule.playlist?.name}</span>
                      <span className="ml-2 text-muted-foreground text-xs">
                        in {schedule.zone?.name} • {schedule.startTime} - {schedule.endTime}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {filteredVoiceMessages.length > 0 && (
                <CommandGroup heading="Voice Messages" forceMount={true}>
                  {filteredVoiceMessages.map((voiceMessage: any) => (
                    <CommandItem
                      key={voiceMessage.id}
                      value={`voice-message-${voiceMessage.id}`}
                      onSelect={() => handleSelect('voiceMessage', voiceMessage.id)}
                      forceMount={true}
                    >
                      <Music className="mr-2 h-4 w-4" />
                      <span>{voiceMessage.displayName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </div>
          ) : (
            <CommandEmpty>
              {loading
                ? "Loading..."
                : searchQuery.length < 2
                ? "Type at least 2 characters to search..."
                : "No results found."
              }
            </CommandEmpty>
          )}
        </CommandList>
      </CommandDialog>
    </>
  )
}
